import get from "lodash/get";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { func, object, bool } from "prop-types";
import { AccessType } from "@quintype/components";
import AccountModal from "../../login/AccountModal";
import { GroupsAndPlansModal, LoginModal, CheckoutModal } from "./Modals";

import "./subscription-page.m.css";

const SubscriptionLayout = ({ initAccessType, initPaytrailPayment, getSubscription, getSubscriptionForUser, validateCoupon, member, isATGlobal }) => {
  const [activeTab, setActiveTab] = useState("subscription");
  const [selectedPlan, setSelectedPlan] = useState({});
  const [selectedSubscriptionGroup, setSelectedSubscriptionGroup] = useState({});
  const [isCheckoutInitialized, setIsCheckoutInitialized] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    if (member && getSubscriptionForUser && isATGlobal) {
      initAccessType(() => {
        getSubscriptionForUser()
          .then((res) => {
            const subscriptions = res?.subscriptions || (Array.isArray(res) ? res : []);
            const activeSubscriptions = subscriptions.filter(
              (plan) => plan.status === "active"
            );
            if (activeSubscriptions.length > 0) {
              setHasActiveSubscription(true);
            }
          })
          .catch((err) => console.error("Error occurred while checking for active subscriptions --->", err));
      });
    }
  }, [member, getSubscriptionForUser, isATGlobal]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const defaultSubscriptionGroup = queryParams.get('group');

    if (member && defaultSubscriptionGroup && !hasActiveSubscription) {
      getSubscription().then((res) => {
        const defaultSelectedOptions = {};

        res.forEach((group, id) => {
          const firstPlan = get(group, ["subscription_plans", "0"]);
          defaultSelectedOptions[group.name] = firstPlan;
        });

        const selectedPlanConfig = defaultSelectedOptions[defaultSubscriptionGroup];
        setActiveTab("checkout");
        setSelectedSubscriptionGroup(defaultSubscriptionGroup);
        setSelectedPlan({ plan: selectedPlanConfig });
        setIsCheckoutInitialized(true);
      });
    }
  }, [member, hasActiveSubscription]);

  useEffect(() => {
    if (activeTab === "checkout" || activeTab === "login") {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 0);
    }
  }, [activeTab]);

  switch (activeTab) {
    case "subscription":
      return (
        <GroupsAndPlansModal
          member={member}
          setActiveTab={setActiveTab}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          getSubscription={getSubscription}
          getSubscriptionForUser={getSubscriptionForUser}
          setSelectedSubscriptionGroup={setSelectedSubscriptionGroup}
          setIsCheckoutInitialized={setIsCheckoutInitialized}
          hasActiveSubscription={hasActiveSubscription}
        />
      );

    case "login":
      return <LoginModal
        setActiveTab={setActiveTab}
        AccountModal={AccountModal}
        selectedPlan={selectedPlan}
        selectedSubscriptionGroup={selectedSubscriptionGroup}
      />;

    case "checkout":
      return (
        <>
          {isCheckoutInitialized && (
            <CheckoutModal
              member={member}
              setActiveTab={setActiveTab}
              initPaytrailPayment={initPaytrailPayment}
              selectedPlan={selectedPlan}
              validateCoupon={validateCoupon}
              selectedSubscriptionGroup={selectedSubscriptionGroup}
              hasActiveSubscription={hasActiveSubscription}
            />
          )}
        </>
      );

    default:
      return <GroupsAndPlansModal
        member={member}
        setActiveTab={setActiveTab}
        getSubscription={getSubscription}
        getSubscriptionForUser={getSubscriptionForUser}
        setSelectedSubscriptionGroup={setSelectedSubscriptionGroup}
        setIsCheckoutInitialized={setIsCheckoutInitialized}
        hasActiveSubscription={hasActiveSubscription}
      />;
  }
};

SubscriptionLayout.propTypes = {
  initAccessType: func,
  initPaytrailPayment: func,
  getSubscription: func,
  getSubscriptionForUser: func,
  validateCoupon: func,
  member: object,
  isATGlobal: bool,
};

export const SubscriptionPage = () => {
  const member = useSelector((state) => get(state, ["member"], null));

  /* const member= {
     "id": 2002282,
     "name": "Khemraj",
     "email": "khem.neupane@fiare.com",
     "created-at": 1729084185495,
     "avatar-url": null,
     "first-name": null,
     "last-name": null,
     "verification-status": "social-google",
     "metadata": null,
     "avatar-s3-key": null,
     "slug": null,
     "communication-email": null,
     "phone-number": null,
     "updated-at": 1737705031051,
     "is-tfa-enabled": false,
     "verified-mediums": [
         "email",
         "social-google"
     ],
     "login-phone-number": null,
     "username": "khem.neupane@fiare.com"
   }*/

  const email = get(member, ["email"], "");
  const phone = get(member, ["metadata", "phone-number"], "");
  const publisherAttributes = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes"], {})
  );
  const { accesstypeConfig, enable_subscription_feature, subscription_test_secret } = publisherAttributes;
  const { key, accessTypeBkIntegrationId, ATBaseUrl } = accesstypeConfig || {};

  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [isATGlobal, setIsATGlobal] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const secretKey = subscription_test_secret;
    const isDeiTest = queryParams.get('dei-test') === secretKey;
    const isGlobalEnabled = enable_subscription_feature === true;
    setIsFeatureEnabled(isGlobalEnabled || (isDeiTest && member));
  }, [member, enable_subscription_feature, subscription_test_secret]);

  if (!isFeatureEnabled) {
    return (
      <SubscriptionLayout
        initAccessType={() => { }}
        initPaytrailPayment={() => { }}
        getSubscription={() => Promise.resolve([])}
        getSubscriptionForUser={() => Promise.resolve([])}
        validateCoupon={() => { }}
        member={member}
      />
    );
  }

  return (
    <>
      <AccessType
        enableAccesstype={true}
        accessTypeKey={key}
        email={email}
        phone={phone}
        prodHost={ATBaseUrl}
        stagingHost={ATBaseUrl}
        accessTypeBkIntegrationId={accessTypeBkIntegrationId}
        onATGlobalSet={() => {
          setIsATGlobal(true);
        }}
      >
        {({ initAccessType, validateCoupon, initPaytrailPayment, getSubscription, getSubscriptionForUser }) => (
          <SubscriptionLayout
            initAccessType={initAccessType}
            initPaytrailPayment={initPaytrailPayment}
            getSubscription={getSubscription}
            getSubscriptionForUser={getSubscriptionForUser}
            validateCoupon={validateCoupon}
            member={member}
            isATGlobal={isATGlobal}
          />
        )}
      </AccessType>
    </>
  );
};
